import React, { useState } from 'react';
import { useTelegram } from '../../hooks/useTelegram';
import { useAppSettings } from '../../stores/appSettings';
import { claimSuperAdmin } from '../../utils/api';

export function DeveloperSuperAdminCard(): React.JSX.Element {
  const { user, platform, initData, haptic, tg } = useTelegram();
  const { realRole, currentRole, setRealRole, setCurrentRole, switchContext } = useAppSettings();

  const [secretKey, setSecretKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const userId = user?.id ? String(user.id) : '';
  const isMax = platform === 'max';
  const isSuperAdmin = realRole === 'admin';

  const handleCopy = () => {
    if (!userId) return;
    haptic.impact('light');
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(userId).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {});
    }
  };

  const handleClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!secretKey.trim()) return;

    setLoading(true);
    setMessage(null);
    haptic.impact('medium');

    try {
      const res = await claimSuperAdmin(initData || '', secretKey.trim());
      if (res.ok) {
        setRealRole('admin');
        setCurrentRole('admin');
        switchContext(null, 'admin', null);
        haptic.success();
        setMessage({ type: 'success', text: '🎉 Права Супер-Администратора успешно активированы! Дашборд открыт.' });
        if (tg?.showAlert) {
          tg.showAlert('🎉 Права Супер-Администратора активированы!');
        }
      } else {
        haptic.error();
        setMessage({ type: 'error', text: res.error.message || 'Неверный секретный ключ' });
      }
    } catch (err: any) {
      haptic.error();
      setMessage({ type: 'error', text: err.message || 'Ошибка соединения с сервером' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 to-indigo-950 text-white rounded-[24px] p-5 shadow-xl border border-indigo-500/20 relative overflow-hidden space-y-4 mb-4">
      {/* Background glow */}
      <div className="absolute top-[-20%] right-[-10%] w-48 h-48 bg-indigo-500/20 blur-[50px] rounded-full pointer-events-none" />

      {/* Header */}
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-amber-500 to-indigo-500 flex items-center justify-center text-xl shadow-md">
            🛡️
          </div>
          <div>
            <h4 className="text-sm font-black uppercase tracking-wider text-white">
              {isSuperAdmin ? 'Супер-Администратор 👑' : 'Разработчик и Владелец'}
            </h4>
            <span className="text-[10px] text-indigo-300">
              Платформа: <strong className="text-white uppercase font-black">{platform}</strong>
            </span>
          </div>
        </div>

        {isSuperAdmin ? (
          <span className="bg-amber-500/20 border border-amber-400/40 text-amber-300 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full shadow-sm">
            SUPER ADMIN 🛡️
          </span>
        ) : (
          <span className="bg-white/10 text-white/70 text-[9px] font-bold px-2 py-0.5 rounded-md">
            Гостевой доступ
          </span>
        )}
      </div>

      {/* User ID Box with 1-click Copy */}
      <div className="bg-black/30 border border-white/10 rounded-2xl p-3.5 flex items-center justify-between relative z-10">
        <div>
          <span className="text-[9px] uppercase tracking-widest text-indigo-300 block font-bold mb-0.5">
            Ваш уникальный ID в {isMax ? 'MAX' : 'Telegram'}
          </span>
          <span className="font-mono text-sm font-bold text-white tracking-wide">
            {userId || 'Определяется...'}
          </span>
        </div>
        <button
          onClick={handleCopy}
          className="bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white text-[11px] font-bold px-3 py-2 rounded-xl transition-all shadow-md flex items-center gap-1.5"
        >
          {copied ? '✅ Скопировано!' : '📋 Скопировать'}
        </button>
      </div>

      {/* Activation form if not yet superadmin */}
      {!isSuperAdmin && (
        <form onSubmit={handleClaim} className="space-y-3 relative z-10 pt-1">
          <p className="text-[11px] text-indigo-200 leading-relaxed">
            Для мгновенного закрепления прав владельца платформы введите секретный ключ:
          </p>

          <div className="flex gap-2">
            <input
              type="password"
              placeholder="Секретный ключ..."
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              className="flex-1 bg-black/40 border border-indigo-400/30 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-indigo-300/40 focus:outline-none focus:border-indigo-400 font-mono"
            />
            <button
              type="submit"
              disabled={loading || !secretKey.trim()}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 active:scale-95 disabled:opacity-50 text-slate-950 font-black text-xs px-4 py-2.5 rounded-xl transition-all shadow-lg flex items-center gap-1 shrink-0"
            >
              {loading ? 'Проверка...' : 'Активировать 🚀'}
            </button>
          </div>

          {message && (
            <div className={`p-3 rounded-xl text-xs font-semibold ${
              message.type === 'success' ? 'bg-emerald-500/20 border border-emerald-400/30 text-emerald-300' : 'bg-rose-500/20 border border-rose-400/30 text-rose-300'
            }`}>
              {message.text}
            </div>
          )}
        </form>
      )}
    </div>
  );
}
